import {graphql, PageProps} from 'gatsby'
import React, {useState} from 'react'

import Card from '@/components/card'
import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {CustomersQuery} from '@/graphql-types'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import Dropdown from '@helios/shared/components/dropdown'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section, Layout, BackgroundWrapper} from '@helios/shared/styles/grid'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

import * as s from './customers.styles'

type Props = PageProps<CustomersQuery>

const Customers: React.FC<Props> = (props) => {
    const [mobileCardsLimit, setMobileCardsLimit] = useState(6)
    const [desktopCardsLimit, setDesktopCardsLimit] = useState(12)

    const data = props.data.allPrismicCustomersPage.nodes[0].data

    type CardOption = typeof data.customer_cards[number]

    const isKeyOfCardOption = (key: string, card: CardOption): key is keyof CardOption =>
        key in card

    const industryOptions = Object.entries(data?.industries[0] || {})
        .map(([value, label]) => ({
            label,
            value,
        }))
        .filter(({value}, i, arr) =>
            // Only show options used by at least one card, OR if the option is 'all'
            data.customer_cards.some(
                (card) => (isKeyOfCardOption(value, card) && card[value]) || value === arr[0].value
            )
        )

    const [selectedIndustry, setSelectedIndustry] = useState(industryOptions?.[0].value)

    if (!data) {
        return handleNoPrismicData()
    }

    const handleShowMore = () => {
        setMobileCardsLimit((limit) => limit + 6)
        setDesktopCardsLimit((limit) => limit + 12)
    }

    const customerCards = data.customer_cards.filter(
        (card) =>
            // Only show cards that include the selected industry, OR if the filter is set to 'all'
            (isKeyOfCardOption(selectedIndustry, card) && card[selectedIndustry]) ||
            selectedIndustry === industryOptions[0].value
    )

    return (
        <>
            <SEO
                title={data.meta_title}
                description={data.meta_description}
                openGraphImageUrl={data.open_graph_image?.url}
            />

            <Header />

            <Main>
                <Section padding="small">
                    <FeatureBlock
                        image={data.hero[0].image}
                        title={data.hero[0].title}
                        body={data.hero[0].body.raw}
                        titleSize="xl"
                    />
                </Section>

                {data.social_proof_logos_section && (
                    <TitleWithIconGridSection
                        title={data.social_proof_logos_section.document.data.title}
                        items={data.social_proof_logos_section.document.data.logos.map((item) => ({
                            icon: item.logo,
                        }))}
                    />
                )}

                <QuoteCarousel
                    items={data.testimonials.map(({testimonial}) => ({
                        image: testimonial.document.data.image,
                        quote: testimonial.document.data.quote,
                        mirrored: true,
                        attribution: {
                            name: testimonial.document.data.attribution_name,
                            title: testimonial.document.data.attribution_title,
                        },
                    }))}
                />

                <BackgroundWrapper color="muted">
                    <s.CardsSection>
                        <Dropdown
                            triggerText={data.industry_select_text}
                            options={industryOptions}
                            value={selectedIndustry}
                            onChange={setSelectedIndustry}
                        />

                        <Layout
                            as={s.CardsGrid}
                            variant="four-equal"
                            alignItems="stretch"
                            mobileLimit={mobileCardsLimit}
                            desktopLimit={desktopCardsLimit}
                            totalCards={customerCards.length}
                        >
                            {customerCards.map((card, i) => (
                                <Card
                                    {...card}
                                    key={`${card.title}-${i}`}
                                    ctaText={data.customer_card_cta_text}
                                    imageFit="cover"
                                />
                            ))}

                            <s.ShowMore onClick={handleShowMore}>
                                {data.show_more_customers_text}
                            </s.ShowMore>
                        </Layout>
                    </s.CardsSection>
                </BackgroundWrapper>

                {hasNonEmptyItems(data.why_pleo) && (
                    <Section>
                        <FeatureBlock
                            image={data.why_pleo[0].image}
                            title={data.why_pleo[0].title}
                            body={data.why_pleo[0].body.raw}
                            bodyProps={{
                                color: 'muted',
                            }}
                            mirrored
                        >
                            <Button
                                variant="outline"
                                as={PrismicLink}
                                {...data.why_pleo[0].cta_link}
                            >
                                {data.why_pleo[0].cta_text}
                            </Button>
                        </FeatureBlock>
                    </Section>
                )}

                {data.social_proof_logos_extended_section && (
                    <TitleWithIconGridSection
                        large
                        highlightColor="yellow"
                        title={data.social_proof_logos_extended_section.document.data.title.raw}
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            (item) => ({
                                icon: item.logo,
                            })
                        )}
                    />
                )}

                {data.social_proof_ratings_section && (
                    <TitleWithIconGridSection
                        large
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.raw}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            (item) => ({
                                icon: item.rating.document.data.logo,
                            })
                        )}
                    />
                )}

                {data.signup_banner && (
                    <SignupBannerSection
                        {...data.signup_banner.document.data}
                        color="pink"
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const query = graphql`
    query Customers($locale: String!) {
        allPrismicCustomersPage(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    hero {
                        title
                        body {
                            raw
                        }
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    social_proof_logos_section {
                        document {
                            ... on PrismicSocialProofLogosSection {
                                data {
                                    title
                                    logos {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    testimonials {
                        testimonial {
                            document {
                                ... on PrismicTestimonial {
                                    id
                                    data {
                                        image {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                        quote
                                        attribution_name
                                        attribution_title
                                        cta_text
                                    }
                                }
                            }
                        }
                    }
                    customer_card_cta_text
                    show_more_customers_text
                    industry_select_text
                    industries {
                        all
                        design
                        financial_services
                        food_and_drink
                        logistics
                        marketing
                        media
                        property
                        sports_and_events
                        technology
                        travel
                    }
                    customer_cards {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body
                        ctaLink: cta_link {
                            ...PrismicLink
                        }
                        design
                        financial_services
                        food_and_drink
                        logistics
                        marketing
                        media
                        property
                        sports_and_events
                        technology
                        travel
                    }
                    why_pleo {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        title
                        body {
                            raw
                        }
                        cta_text
                        cta_link {
                            ...PrismicLink
                        }
                    }
                    social_proof_logos_extended_section {
                        document {
                            ... on PrismicSocialProofLogosExtendedSection {
                                data {
                                    title {
                                        raw
                                    }
                                    logos {
                                        logo {
                                            alt
                                            copyright
                                            url
                                            dimensions {
                                                height
                                                width
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    social_proof_ratings_section {
                        document {
                            ... on PrismicSocialProofRatingsSection {
                                data {
                                    title {
                                        raw
                                    }
                                    ratings {
                                        rating {
                                            document {
                                                ... on PrismicRating {
                                                    data {
                                                        logo {
                                                            alt
                                                            copyright
                                                            url
                                                            dimensions {
                                                                height
                                                                width
                                                            }
                                                        }
                                                        stars
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    signup_banner {
                        document {
                            ...SignupBanner
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(Customers)
